/**
 * @file 取り組みの記録 (毎日の進捗と様子のメモ)。
 *
 * @what: その日の記録を 1 件ずつ残すフォーム (日付・教材・ページ・様子) と、直近の記録の一覧。
 *   ページを入れると教材の「いまのページ」もサーバ側で一緒に進む (後退はしない)。一覧の行は
 *   「なおす」で開く枠から、あとから直せる (写真を足すのもここ)。
 * @why: チェックポイント (確認テスト) の間の日々を見えるようにする (代表 2026-08-20)。
 *   送信は「足す」ボタンだけで行う (Enter 送信は漢字変換の確定と衝突するためやめた。代表 2026-08-24)。
 *   記録は append 型なので保存バーを介さず、その場で確定する。あとからの直しは同じ id への
 *   送り直し (サーバが上書きとして受ける) で、行の中で完結させる (代表 2026-08-28)。
 * @failure: 追加・削除の失敗は文言のみ表示 (詳細は console)。日付が空・不正のときは
 *   追加ボタンを無効にする。
 */

import { useRef, useState } from "react";
import { formatLogDate, todayLocal } from "../api/dates";
import {
  newProgressLogId,
  type StudyMaterialInput,
  type StudyProgressLog,
  type StudyProgressLogInput,
} from "../api/plans";
import type { ProgressLogsState } from "../hooks/useProgressLogs";
import { uploadStudyLogImage } from "../lib/studyLogImage";
import { LogEditForm } from "./LogEditForm";
import { LogImageThumb } from "./StudyLogImage";
import "./study.css";

const formatDate = (date: string) => formatLogDate(date, false);

export function ProgressLogCard({
  familyId,
  materials,
  logsState,
  onAdd,
  onRemove,
  onLoadMore,
  busy,
  actionError,
}: {
  /** 添付画像のアップロード先 (families/{familyId}/study_log_images/)。 */
  familyId: string;
  materials: StudyMaterialInput[];
  logsState: ProgressLogsState;
  onAdd: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  onRemove: (logId: string) => void;
  onLoadMore: () => void;
  busy: boolean;
  actionError: string | null;
}) {
  const [date, setDate] = useState(todayLocal);
  const [materialId, setMaterialId] = useState("");
  const [fromPage, setFromPage] = useState("");
  const [toPage, setToPage] = useState("");
  const [memo, setMemo] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // 直している最中の記録 (1 行だけ開く)
  const [editingId, setEditingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const materialTitleById = new Map(materials.map((m) => [m.id, m.title]));
  // 選ぶ機会の多い順に並べる (使っている → 用意した → 検討中 → 使い終えた)。除外は出さない。
  // 同じ状態の中は登録順のまま (代表 2026-08-24)。
  const pickOrder: Record<string, number> = { in_use: 0, ready: 1, considering: 2, finished: 3 };
  const selectable = materials
    .filter((m) => m.status !== "excluded")
    .map((m, i) => ({ m, i }))
    .sort((a, b) => (pickOrder[a.m.status] ?? 9) - (pickOrder[b.m.status] ?? 9) || a.i - b.i)
    .map(({ m }) => m);
  const canSubmit =
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    !busy &&
    // from を入れたら to も必要 (サーバの検証と同じ)
    !(fromPage.trim() !== "" && toPage.trim() === "");

  const clearPhoto = () => {
    setPhoto(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    if (!canSubmit) return;
    const input: StudyProgressLogInput = { id: newProgressLogId(), date };
    if (materialId) input.material_id = materialId;
    const from = Number(fromPage);
    const to = Number(toPage);
    if (fromPage.trim() !== "" && Number.isFinite(from) && from > 0) input.from_page = from;
    if (toPage.trim() !== "" && Number.isFinite(to) && to > 0) input.to_page = to;
    if (memo.trim()) input.memo = memo.trim();
    setUploadError(null);
    if (photo) {
      // 記録より先に画像を上げる (失敗したら記録も作らない = 参照切れを作らない)
      try {
        input.image_path = await uploadStudyLogImage(photo, familyId, input.id);
      } catch (err) {
        console.error("[study] failed to upload log image", err);
        setUploadError("写真を送れませんでした。写真なしで記録するか、時間を置いてお試しください。");
        return;
      }
    }
    const created = await onAdd(input);
    if (created) {
      setFromPage("");
      setToPage("");
      setMemo("");
      clearPhoto();
    }
  };

  return (
    <section className="study-log-card" aria-label="取り組みの記録">
      <div className="study-log-form">
        <div className="study-log-form-row">
          <label className="study-log-field">
            <span>日付</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="study-log-field">
            <span>教材</span>
            <select value={materialId} onChange={(e) => setMaterialId(e.target.value)}>
              <option value="">（教材によらない記録）</option>
              {selectable.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.status === "in_use" ? `${m.title}（使っている）` : m.title}
                </option>
              ))}
            </select>
          </label>
          <label className="study-log-field study-log-field--page">
            <span>ページ</span>
            <span className="study-log-pages">
              <input
                type="number"
                min={1}
                max={9999}
                placeholder="20"
                aria-label="始めたページ"
                value={fromPage}
                onChange={(e) => setFromPage(e.target.value)}
              />
              <span className="study-muted">〜</span>
              <input
                type="number"
                min={1}
                max={9999}
                placeholder="24"
                aria-label="進んだページ"
                value={toPage}
                onChange={(e) => setToPage(e.target.value)}
              />
            </span>
          </label>
        </div>
        <label className="study-log-field">
          <span>様子</span>
          <input
            type="text"
            placeholder="例: 楽しそうにやっていた。最初は詰まったが乗り越えた"
            maxLength={500}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
          />
        </label>
        <div className="study-log-form-actions">
          <label className="study-log-photo-field">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            />
            <span className="study-ghost-btn" role="button">
              {photo ? "写真を変える" : "写真を足す"}
            </span>
          </label>
          {photo ? (
            <span className="study-log-photo-picked">
              {photo.name}
              <button type="button" className="study-link-btn" onClick={clearPhoto}>
                外す
              </button>
            </span>
          ) : null}
          <button
            type="button"
            className="study-save-btn"
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            記録する
          </button>
          {actionError || uploadError ? (
            <span role="alert" className="study-save-error">
              {uploadError ?? actionError}
            </span>
          ) : null}
        </div>
      </div>

      {logsState.status === "loading" ? (
        <p role="status" className="study-muted">
          記録を読み込んでいます...
        </p>
      ) : logsState.status === "error" ? (
        <p role="alert" className="study-error">
          記録を読み込めませんでした。時間を置いて再読み込みしてください。
        </p>
      ) : logsState.items.length === 0 ? (
        <p className="study-muted">まだ記録がありません。</p>
      ) : (
        <>
          <ul className="study-log-list">
            {logsState.items.map((l) =>
              editingId === l.id ? (
                <li key={l.id} className="study-log-row study-log-row--editing">
                  <LogEditForm
                    log={l}
                    familyId={familyId}
                    kind="study"
                    materials={selectable}
                    busy={busy}
                    onSave={onAdd}
                    onClose={() => setEditingId(null)}
                  />
                </li>
              ) : (
                <li key={l.id} className="study-log-row">
                  <span className="study-log-date">{formatDate(l.date)}</span>
                  <span className="study-log-body">
                    {l.material_id ? (
                      <span className="study-log-material">
                        {materialTitleById.get(l.material_id) ?? "（削除した教材）"}
                      </span>
                    ) : null}
                    {l.to_page ? (
                      <span className="study-log-pages-label">
                        {l.from_page ? `p.${l.from_page}〜${l.to_page}` : `p.${l.to_page} まで`}
                      </span>
                    ) : null}
                    {l.memo ? <span className="study-log-memo">{l.memo}</span> : null}
                    {l.image_path ? <LogImageThumb path={l.image_path} enc={l.image_enc} /> : null}
                  </span>
                  <span className="study-log-row-actions">
                    <button
                      type="button"
                      className="study-link-btn"
                      aria-label={`${formatDate(l.date)} の記録をなおす`}
                      onClick={() => setEditingId(l.id)}
                    >
                      なおす
                    </button>
                    <button
                      type="button"
                      className="study-material-del"
                      aria-label={`${formatDate(l.date)} の記録を削除`}
                      onClick={() => onRemove(l.id)}
                    >
                      削除
                    </button>
                  </span>
                </li>
              ),
            )}
          </ul>
          {logsState.nextCursor ? (
            <button type="button" className="study-ghost-btn" disabled={busy} onClick={onLoadMore}>
              もっと見る
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
